import $ from 'jquery';
import _ from 'underscore';
import JSOG from 'jsog';
import { arrayify, paramify } from '../Etc';
import Promise from 'bluebird';
import { requestHeaders } from '../constants';
import join from 'url-join';

/**
 * This helper method does a GET to the passed in resource URL with
 some params and sets a cache header if set
 * @param resource
 * @param params
 * @param refresh
 * @param defaultMessage
 * @returns {*}
 */
export const getErrorMessage = (jqXhr, defaultMessage = 'Unknown network error occurred.') => {
  const hasErrors = jqXhr && jqXhr.responseJSON && jqXhr.responseJSON.errors;
  const errors = hasErrors ? jqXhr.responseJSON.errors : null;
  if (errors) {
    return _.map(errors, (err) => {
      const attr = err.attribute;
      const message = err.message;
      let toReturn = message;
      if (attr) {
        toReturn = `${attr}: ${message}`;
      }

      return toReturn;
    }).join('; ');
  } else {
    return defaultMessage;
  }
};

export const getError = (jqXhr, defaultMessage = 'Unknown Error!') => {
  return new Error(getErrorMessage(jqXhr, defaultMessage));
};

export const servletGet = (resource, params = {}) => {
  return new Promise((resolve, reject, onCancel) => {
    const success = (response, status, jqXhr) => {
      const totalCountHeader = jqXhr.getResponseHeader(requestHeaders.TOTAL_RECORDS_COUNT_HEADER);
      const maxCountHeader = jqXhr.getResponseHeader(requestHeaders.MAX_COUNT_HEADER);
      let totalCount = totalCountHeader ? +totalCountHeader : null;
      let maxCount = maxCountHeader ? +maxCountHeader : null;

      if (totalCount !== null && isNaN(totalCount)) {
        totalCount = null;
      }

      if (maxCount !== null && isNaN(maxCount)) {
        maxCount = null;
      }

      resolve({ results: JSOG.decode(response), totalCount, maxCount });
    };

    const failure = (jqXhr) => {
      reject(getError(jqXhr, `Failed to get ${resource}`));
    };

    const request = $.post({
      data: paramify(params),
      url: join(resource, '_get')
    });

    request.then(success, failure);

    onCancel(() => request.abort());
  });
};

// only get the results out of the servlet get for backwards compatibility
export const servletGetResults = (resource, params, refresh) => {
  return servletGet(resource, params, refresh).then((object) => object.results);
};

export const servletGetAll = (resource, params) => {
  const originalSort = (params && params.sort) ? arrayify(params.sort) : [];

  // we must have a sort on a unique element to get stably sorted list
  const fetchParams = _.omit({ ...params, sort: [ ...originalSort, 'A|id' ] }, 'count', 'start');

  return new Promise((resolve, reject, onCancel) => {
    const firstPage = servletGet(resource, fetchParams);

    onCancel(() => firstPage.cancel());

    firstPage.then(({ results, maxCount, totalCount }) => {
      if (totalCount === null || totalCount === results.length) {
        resolve(results);
      } else {
        const numPages = Math.ceil(totalCount / maxCount);
        const requests = _.map(
          _.range(1, numPages),
          // map each page to a function that requests that page
          pageNo => servletGetResults(resource, {
            ...fetchParams,
            count: maxCount,
            start: maxCount * pageNo
          })
        );

        // queue up page loads
        Promise.all(requests)
          .then(
            otherPageData => {
              const allResults = results.concat(_.flatten(otherPageData, true));
              const numIds = _.uniq(_.pluck(allResults, 'id')).length;

              if (numIds !== totalCount) {
                reject(new Error(
                  `Total number of results inconsistent with total count header: 
                    ${totalCount} expected, found ${numIds}`)
                );
              } else {
                resolve(allResults);
              }
            },
            err => reject(new Error(`At least one page request failed: ${err.message}`))
          );
      }
    });
  });
};

export const servletPost = (url, object = null, queryParams = null, contentType = 'application/json') => {
  return new Promise((resolve, reject) => {
    let method = 'POST';

    if (queryParams) {
      url = `${url}?${paramify(queryParams)}`;
    }

    $.ajax({
      method,
      url,
      data: JSON.stringify(object),
      contentType
    }).then((saved) => {
      resolve(JSOG.decode(saved));
    }, (jqXhr) => reject(getError(jqXhr)));
  });
};

export const servletSave = (url, object = null, queryParams = null, contentType = 'application/json') => {
  return new Promise((resolve, reject) => {
    let method = 'POST';
    if (object && object.id) {
      method = 'PUT';
      url = join(url, object.id);
    }

    if (queryParams) {
      url = `${url}?${paramify(queryParams)}`;
    }

    $.ajax({
      method,
      url,
      data: JSOG.stringify(object),
      contentType
    }).then((saved) => {
      resolve(JSOG.decode(saved));
    }, (jqXhr) => reject(getError(jqXhr)));
  });
};


export const servletDelete = (url, idOrParams, additionalParams = null) => {
  if (typeof idOrParams !== 'string' && typeof idOrParams !== 'object') {
    return Promise.reject(new Error('idOrParams was not an object or a string'));
  }

  return new Promise((resolve, reject) => {
    if (typeof idOrParams === 'object') {
      url = `${url}?${paramify({ ...idOrParams, ...additionalParams })}`;
    } else if (typeof idOrParams === 'string' && idOrParams.length > 0) {
      url = `${url}/${idOrParams}?${paramify(additionalParams)}`;
    }

    $.ajax({
      method: 'DELETE',
      url
    }).then(() => resolve(), (jqXhr) => reject(getError(jqXhr)));
  });
};

export default {
  getError,
  getErrorMessage,
  servletGet,
  servletGetAll,
  servletGetResults,
  servletSave,
  servletPost,
  servletDelete
};



// WEBPACK FOOTER //
// ./src/js/util/dao/helpers.js